import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import Breadcrumbs from "../../components/shared/Breadcrumbs.jsx";
import ShareLinks from "../../components/shared/ShareLinks.jsx";
import EventGallery from "../../components/events/EventGallery.jsx";
import { events, getEventBySlug, findByLangAndId, excerpt, formatDate } from "../../utils/content.js";
import { buildEventJsonLd, buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import { eventCategoryLabel } from "../../utils/eventCategories.js";
import NotFound from "../NotFound.jsx";

export default function EventDetail({ lang }) {
  const { slug } = useParams();
  const { t } = useTranslation();
  const event = getEventBySlug(lang, slug);

  if (!event) return <NotFound lang={lang} />;

  const { frontmatter: f, content } = event;
  const otherLang = lang === "de" ? "en" : "de";
  // Sibling found by `id`, not by assuming the same slug — events happen
  // to share slugs across languages today, but nothing requires it.
  const sibling = findByLangAndId(events, otherLang, f.id);
  const altPath = sibling ? `/events/${sibling.frontmatter.slug}` : `/events/${slug}`;
  const hasTranslation = Boolean(sibling);
  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.events"), path: "/events" },
    { name: f.title, path: `/events/${slug}` },
  ];

  return (
    <>
      <SEOHead
        lang={lang}
        path={`/events/${slug}`}
        altPath={altPath}
        hasTranslation={hasTranslation}
        title={f.title}
        category={eventCategoryLabel(f.category, lang)}
        description={excerpt(content)}
        image={f.image}
      />
      <JsonLd data={buildEventJsonLd(f, lang)} />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "40px", padding: "32px 40px 96px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />

          <div className="tl-diagonal" style={{ display: "flex", flexDirection: "column", gap: "22px", padding: "56px 40px", borderRadius: "14px" }}>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <span className="tl-badge" style={{ background: "var(--color-gold)" }}>
                {formatDate(f.date, lang)}
                {f.endDate ? ` – ${formatDate(f.endDate, lang)}` : ""}
              </span>
              <span style={{ fontSize: "12px", color: "var(--color-cream-soft)", border: "1px solid rgba(254,250,239,0.25)", padding: "5px 12px", borderRadius: "999px" }}>
                {eventCategoryLabel(f.category, lang)}
              </span>
              {f.ageRestriction != null && (
                <span style={{ fontSize: "12px", color: "var(--color-cream-soft)", border: "1px solid rgba(254,250,239,0.25)", padding: "5px 12px", borderRadius: "999px" }}>
                  {lang === "de" ? `ab ${f.ageRestriction} Jahren` : `${f.ageRestriction}+`}
                </span>
              )}
              {f.externalOrga && (
                <span style={{ fontSize: "12px", color: "var(--color-cream-soft)", border: "1px solid rgba(254,250,239,0.25)", padding: "5px 12px", borderRadius: "999px" }}>
                  {f.orgaName ? `${t("events.externalOrgaBadgeWith")} ${f.orgaName}` : t("events.externalOrgaBadge")}
                </span>
              )}
              {f.tags?.map((tag) => (
                <span key={tag} style={{ fontSize: "12px", color: "var(--color-cream-soft)", border: "1px solid rgba(254,250,239,0.25)", padding: "5px 12px", borderRadius: "999px" }}>
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "46px", lineHeight: 1.08, color: "var(--color-cream)", maxWidth: "22ch", margin: 0 }}>
              {f.title}
            </h1>
            <p style={{ fontSize: "16px", color: "var(--color-cream-soft)", margin: 0 }}>{f.location}</p>
            {f.publishedAt && (
              <span style={{ fontSize: "13px", color: "var(--color-cream-faint)" }}>
                {t("common.publishedOn", { date: formatDate(f.publishedAt, lang) })}
                {f.updatedAt && f.updatedAt !== f.publishedAt
                  ? ` · ${t("common.updatedOn", { date: formatDate(f.updatedAt, lang) })}`
                  : ""}
              </span>
            )}
            <ShareLinks shortUrl={f.shortUrl} title={f.title} />
          </div>

          <div className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: "32px", alignItems: "start" }}>
            <article style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)" }}>
              <ReactMarkdown>{content}</ReactMarkdown>
            </article>

            <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "28px", position: "sticky", top: "96px" }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)" }}>{t("events.detailsCta")}</span>

              {f.tickets?.length > 0 ? (
                f.tickets.map((ticket, i) => (
                  <div
                    key={ticket.type}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      background: i === 0 ? "rgba(10,9,7,0.05)" : "rgba(206,149,50,0.2)",
                      borderRadius: "10px",
                      padding: "16px 18px",
                    }}
                  >
                    <span style={{ fontSize: "15px", color: "var(--color-ink)" }}>{ticket.label}</span>
                    <span style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-ink)" }}>{ticket.price} €</span>
                  </div>
                ))
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", background: "rgba(10,9,7,0.05)", borderRadius: "10px", padding: "16px 18px" }}>
                    <span style={{ fontSize: "15px", color: "var(--color-ink)" }}>{t("events.priceFrom")}</span>
                    <span style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-ink)" }}>{f.price} €</span>
                  </div>
                  {f.priceReduced != null && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", background: "rgba(206,149,50,0.2)", borderRadius: "10px", padding: "16px 18px" }}>
                      <span style={{ fontSize: "15px", color: "var(--color-ink)" }}>{t("events.reduced")}</span>
                      <span style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-ink)" }}>{f.priceReduced} €</span>
                    </div>
                  )}
                </>
              )}
              {f.registrationOpen && f.registrationUrl ? (
                <a href={f.registrationUrl} target="_blank" rel="noreferrer" className="tl-pill tl-pill-dark" style={{ width: "100%", padding: "15px 0" }}>
                  {t("events.registrationCta")}
                </a>
              ) : (
                <span className="tl-pill tl-pill-ghost-dark" style={{ width: "100%", padding: "13px 0" }}>
                  {t("events.registrationCta")}
                </span>
              )}
            </div>
          </div>

          <EventGallery images={f.gallery} />

          <Link to={`/${lang}/events`} style={{ fontSize: "13px", color: "var(--color-cream-soft)" }}>
            ← {t("nav.events")}
          </Link>
        </div>
      </div>
    </>
  );
}
